// src/components/CreateUserDialog.js
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import { createUser } from '../services/userService';

const EMPTY_FORM = {
  serviceNumber: '',
  email: '',
  firstName: '',
  lastName: '',
  role: '',
  assignedZone: '',
  password: '',
  confirmPassword: '',
};

const zones = [
  'ZONE A - LAGOS',
  'ZONE B - KADUNA',
  'ZONE B2 - KADUNA',
  'ZONE C - BAUCHI',
  'ZONE D - MINNA',
  'ZONE E - OWERRI',
  'ZONE F - IBADAN',
  'ZONE G - BENIN',
  'ZONE H - BENUE',
  'SERVICE HEADQUARTERS',
];

const CreateUserDialog = ({ open, onClose, onSuccess, onNotify }) => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError('');

    // Frontend validation
    if (!formData.serviceNumber.trim()) {
      setError('Service number is required');
      return;
    }

    const snRegex = /^[A-Za-z0-9\/\-]+$/;
    if (!snRegex.test(formData.serviceNumber.trim())) {
      setError('Service number can only contain letters, numbers, slashes, and hyphens');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.role === 'VERIFICATION_OFFICER' && !formData.assignedZone) {
      setError('Please select a zone for verification officers');
      return;
    }

    setLoading(true);

    try {
      await createUser({
        serviceNumber: formData.serviceNumber.trim().toUpperCase(),
        email:         formData.email,
        firstName:     formData.firstName,
        lastName:      formData.lastName,
        role:          formData.role,
        assignedZone:  formData.assignedZone || null,
        password:      formData.password,
      });

      const fullName = `${formData.firstName} ${formData.lastName}`.trim();
      setFormData(EMPTY_FORM);
      if (onNotify) onNotify(`User ${fullName} created successfully`, 'success');
      onSuccess();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to create user';
      setError(msg);
      if (onNotify) onNotify(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData(EMPTY_FORM);
      setError('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New User</DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Service Number — first field, required for all roles */}
          <TextField
            fullWidth
            label="Service Number"
            name="serviceNumber"
            value={formData.serviceNumber}
            onChange={handleChange}
            required
            margin="normal"
            disabled={loading}
            placeholder="e.g. NIS/2024/001"
            helperText="Unique service number for this user — will be stored in uppercase"
            inputProps={{ style: { textTransform: 'uppercase' } }}
          />

          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            margin="normal"
            disabled={loading}
          />

          <TextField
            fullWidth
            label="First Name"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            required
            margin="normal"
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Last Name"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            required
            margin="normal"
            disabled={loading}
          />

          <TextField
            fullWidth
            select
            label="Role"
            name="role"
            value={formData.role}
            onChange={handleChange}
            required
            margin="normal"
            disabled={loading}
          >
            <MenuItem value="ADMIN">Admin</MenuItem>
            <MenuItem value="VERIFICATION_OFFICER">Verification Officer</MenuItem>
            <MenuItem value="MANAGEMENT">Management</MenuItem>
          </TextField>

          {/* Zone — only shown for Verification Officers */}
          {formData.role === 'VERIFICATION_OFFICER' && (
            <TextField
              fullWidth
              select
              label="Assigned Zone"
              name="assignedZone"
              value={formData.assignedZone}
              onChange={handleChange}
              required
              margin="normal"
              disabled={loading}
            >
              {zones.map((zone) => (
                <MenuItem key={zone} value={zone}>
                  {zone}
                </MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            fullWidth
            label="Password"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleChange}
            required
            margin="normal"
            disabled={loading}
            helperText="Minimum 8 characters, at least 1 letter and 1 number"
          />

          <TextField
            fullWidth
            label="Confirm Password"
            name="confirmPassword"
            type="password"
            value={formData.confirmPassword}
            onChange={handleChange}
            required
            margin="normal"
            disabled={loading}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Create User'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateUserDialog;